import React, { useState } from "react";
import { Button, Form, Modal, Alert, Spinner } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation } from "react-query";
import { toast } from "react-toastify";
import { SupportService } from "../../services/support";

export enum DeliveryIssueReason {
  REFUND_REQUIRED = "refund_required",
  DAMAGED_PACKAGE = "damaged_package",
  WRONG_ITEM = "wrong_item",
  LATE_DELIVERY = "late_delivery",
  MISSING_ITEMS = "missing_items",
  TECHNICAL_ISSUE = "technical_issue",
  OTHER = "other",
}

interface ReportDeliveryIssueProps {
  show: boolean;
  onHide: () => void;
  orderId: string;
  orderCode?: string;
}

const ReportDeliveryIssue: React.FC<ReportDeliveryIssueProps> = ({
  show,
  onHide,
  orderId,
  orderCode,
}) => {
  const intl = useIntl();
  const [reason, setReason] = useState<DeliveryIssueReason | "">("");
  const [description, setDescription] = useState("");

  const reportMutation = useMutation(
    (data: {
      orderId: string;
      reason: DeliveryIssueReason;
      description?: string;
    }) => SupportService.reportDeliveryIssue(data),
    {
      onSuccess: () => {
        toast.success(
          intl.formatMessage({ id: "Delivery issue reported successfully" }),
        );
        handleClose();
      },
      onError: (error: any) => {
        toast.error(
          error.response?.data?.message ||
            intl.formatMessage({ id: "Error reporting delivery issue" }),
        );
      },
    },
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason) return;

    reportMutation.mutate({
      orderId,
      reason: reason as DeliveryIssueReason,
      description: description.trim() || undefined,
    });
  };

  const handleClose = () => {
    setReason("");
    setDescription("");
    onHide();
  };

  const reasonOptions = [
    {
      value: DeliveryIssueReason.REFUND_REQUIRED,
      label: intl.formatMessage({ id: "The order has to be refunded" }),
    },
    {
      value: DeliveryIssueReason.DAMAGED_PACKAGE,
      label: intl.formatMessage({ id: "Damaged package" }),
    },
    {
      value: DeliveryIssueReason.WRONG_ITEM,
      label: intl.formatMessage({ id: "Wrong item received" }),
    },
    {
      value: DeliveryIssueReason.LATE_DELIVERY,
      label: intl.formatMessage({ id: "Late delivery" }),
    },
    {
      value: DeliveryIssueReason.MISSING_ITEMS,
      label: intl.formatMessage({ id: "Missing items" }),
    },
    {
      value: DeliveryIssueReason.TECHNICAL_ISSUE,
      label: intl.formatMessage({ id: "Technical issue" }),
    },
    {
      value: DeliveryIssueReason.OTHER,
      label: intl.formatMessage({ id: "Other" }),
    },
  ];

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <em className="icon ni ni-alert-circle me-2 text-warning" />
          <FormattedMessage id="Report Delivery Issue" />
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Alert variant="info" className="mb-4">
            <strong>
              <FormattedMessage id="Order" />: #{orderCode || orderId}
            </strong>
            <br />
            <small>
              <FormattedMessage id="Please describe the issue with your delivery so we can assist you better" />
            </small>
          </Alert>

          <Form.Group className="mb-3">
            <Form.Label>
              <FormattedMessage id="Issue Type" /> *
            </Form.Label>
            <Form.Select
              value={reason}
              onChange={(e) => setReason(e.target.value as DeliveryIssueReason)}
              required
              disabled={reportMutation.isLoading}
            >
              <option value="">
                {intl.formatMessage({ id: "Select an issue type" })}
              </option>
              {reasonOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              <FormattedMessage id="Description" />
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={intl.formatMessage({
                id: "Please provide additional details about the issue...",
              })}
              disabled={reportMutation.isLoading}
            />
            <Form.Text className="text-muted">
              <FormattedMessage id="Optional: Provide more details to help us resolve the issue faster" />
            </Form.Text>
          </Form.Group>

          {reportMutation.isError && (
            <Alert variant="danger">
              <FormattedMessage id="There was an error reporting the issue. Please try again." />
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={reportMutation.isLoading}
          >
            <FormattedMessage id="Cancel" />
          </Button>
          <Button
            type="submit"
            variant="warning"
            disabled={!reason || reportMutation.isLoading}
          >
            {reportMutation.isLoading ? (
              <>
                <Spinner size="sm" className="me-2" />
                <FormattedMessage id="Reporting..." />
              </>
            ) : (
              <>
                <em className="icon ni ni-send me-2" />
                <FormattedMessage id="Report Issue" />
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ReportDeliveryIssue;
